"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Lock, Monitor, Cpu, Layers, Calendar } from "lucide-react";
import ParticlesBackground from "@/components/ParticlesBackground";
import BookingModal from "@/components/BookingModal";
import { useState, useEffect } from "react";

export default function Home() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [depth, setDepth] = useState(0);

  const openBooking = (source: string) => {
    const w = window as Window & { dataLayer?: Array<Record<string, unknown>> };
    if (w.dataLayer) {
      w.dataLayer.push({
        event: "booking_modal_opened",
        source,
      });
    }
    setIsBookingOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      const newDepth = Math.floor(scrollPercent * -520);
      setDepth(newDepth);

      const isLight = document.documentElement.classList.contains("light");
      const absDepth = Math.abs(newDepth);
      if (isLight) {
        if (absDepth > 350) {
          document.documentElement.style.setProperty('--grid-color', 'rgba(94, 23, 235, 0.08)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(94, 23, 235, 0.12)');
        } else if (absDepth > 150) {
          document.documentElement.style.setProperty('--grid-color', 'rgba(94, 23, 235, 0.05)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(94, 23, 235, 0.08)');
        } else {
          document.documentElement.style.setProperty('--grid-color', 'rgba(94, 23, 235, 0.03)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(94, 23, 235, 0.05)');
        }
      } else {
        if (absDepth > 350) {
          document.documentElement.style.setProperty('--grid-color', 'rgba(139, 92, 246, 0.08)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(139, 92, 246, 0.3)');
        } else if (absDepth > 150) {
          document.documentElement.style.setProperty('--grid-color', 'rgba(139, 92, 246, 0.05)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(139, 92, 246, 0.2)');
        } else {
          document.documentElement.style.setProperty('--grid-color', 'rgba(139, 92, 246, 0.03)');
          document.documentElement.style.setProperty('--accent-glow', 'rgba(139, 92, 246, 0.15)');
        }
      }

      const nav = document.querySelector('nav');
      if (nav) {
        if (window.scrollY > 50) {
          nav.classList.remove('hero-top-nav');
        } else {
          nav.classList.add('hero-top-nav');
        }
      }
    };
    
    // Initial check on mount
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.removeProperty('--grid-color');
      document.documentElement.style.removeProperty('--accent-glow');
      const nav = document.querySelector('nav');
      if (nav) {
        nav.classList.remove('hero-top-nav');
      }
    };
  }, []);

  return (
    <>
      <ParticlesBackground />

      <div className="depth-counter font-mono">
        <span className="depth-value">{depth === 0 ? "0M" : `${depth}M`}</span>
        <span className="depth-label">DEPTH</span>
      </div>

      <main>
        {/* Dynamic Cinematic Hero */}
        <section id="hero" className="hero">
          <div className="cinematic-bg">
            <Image
              src="/cinematic_carbon_mesh.png"
              alt="Cinematic Hero Background"
              fill
              priority
              quality={90}
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
          <div className="cinematic-overlay"></div>
          
          <div className="container flex flex-col items-center hero-content">
            <span className="section-tag font-mono">GROWTH-DRIVEN DIGITAL SYSTEMS</span>
            <h1 className="hero-title font-mono">
              BUILD THE <span>ENGINE FOR GROWTH</span>
            </h1>
            <p className="hero-subhead">
              We help founders launch SaaS MVPs, teams deploy enterprise AI, and growing businesses build scalable product systems that turn technical complexity into measurable momentum.
            </p>
            <div className="flex gap-4 font-mono mt-4">
              <button onClick={() => openBooking("hero")} className="btn btn-primary px-8 py-3 text-base uppercase tracking-wider">
                BOOK STRATEGY CALL
              </button>
              <Link href="/#contact" className="btn btn-outline px-8 py-3 text-base uppercase tracking-wider">
                GET A QUOTE
              </Link>
            </div>

            <div className="hero-proof-grid">
              <div className="hero-proof-item">
                <strong>2–6 weeks</strong>
                <span>from first brief to launch-ready MVP</span>
              </div>
              <div className="hero-proof-item">
                <strong>24h</strong>
                <span>average response time for inbound projects</span>
              </div>
              <div className="hero-proof-item">
                <strong>Built to scale</strong>
                <span>web, AI, and backend systems designed for growth</span>
              </div>
            </div>
          </div>
        </section>

        <section id="services">
          <div className="container">
            <div className="section-header">
              <span className="section-tag font-mono">WHAT WE DELIVER</span>
              <h2 className="section-title font-mono">SYSTEMS BUILT FOR REAL BUSINESS RESULTS</h2>
            </div>

            <div className="grid">
              <Link href="/services/saas-mvp-development" className="glass-card block text-inherit no-underline">
                <div className="card-icon"><Monitor className="w-6 h-6" /></div>
                <h3 className="card-title font-mono">SaaS MVP Development</h3>
                <p className="card-text">Launch-ready SaaS products, customer portals, and dashboards built to validate demand without wasting runway.</p>
              </Link>
              <Link href="/services/ai-integrations" className="glass-card block text-inherit no-underline">
                <div className="card-icon"><Cpu className="w-6 h-6" /></div>
                <h3 className="card-title font-mono">AI Integrations</h3>
                <p className="card-text">Workflow automation, smarter operations, and AI features that reduce repetitive work and improve decision speed.</p>
              </Link>
              <Link href="/services/backend-systems" className="glass-card block text-inherit no-underline">
                <div className="card-icon"><Layers className="w-6 h-6" /></div>
                <h3 className="card-title font-mono">Backend Systems</h3>
                <p className="card-text">Scalable infrastructure, data flows, and integrations that support your product as demand grows.</p>
              </Link>
            </div>
          </div>
        </section>

        <section id="story" className="story-section">
          <div className="container story-grid">
            <div className="story-content">
              <span className="section-tag font-mono">WHY TEAMS CHOOSE US</span>
              <h2 className="section-title font-mono">DEEP ROOTS. SHARP EXECUTION.</h2>
              <p>
                We combine engineering depth with business clarity. That means fewer delays, cleaner technical decisions, and digital systems designed around outcomes instead of vanity metrics.
              </p>
              <p>
                Whether you need a launch-ready product, a custom internal workflow, or AI automations that save hours each week, we build the structure that makes growth sustainable.
              </p>
              <p>
                Planning a SaaS launch? Start with our <Link href="/services/saas-mvp-development" className="text-primary-glow font-semibold hover:underline">SaaS MVP development</Link> service for scope, timeline, and cost guidance. For enterprise operations, explore our <Link href="/services/ai-integrations" className="text-primary-glow font-semibold hover:underline">AI agents and RAG chatbot systems</Link>.
              </p>
              <p>
                From architecture to user experience, we focus on what matters: performance, reliability, and a clearer path from idea to revenue.
              </p>
            </div>
            <div className="story-visual" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Image
                src="/coal_52.png"
                alt="52 Coal Emblem"
                width={364}
                height={364}
                quality={90}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 40px rgba(239, 68, 68, 0.1)',
                  filter: 'drop-shadow(0px 0px 20px rgba(239, 68, 68, 0.2))'
                }}
              />
            </div>
          </div>
        </section>

        <section id="projects">
          <div className="container">
            <div className="section-header">
              <span className="section-tag font-mono">CASE STUDIES</span>
              <h2 className="section-title font-mono">SYSTEMS THAT SOLVE REAL PROBLEMS</h2>
            </div>

            <Link
              href="/sanadcom-ae"
              className="project-card block text-inherit no-underline"
              onClick={() => {
                const w = window as Window & { dataLayer?: Array<Record<string, unknown>> };
                if (w.dataLayer) {
                  w.dataLayer.push({
                    event: "case_study_click",
                    destination: "/sanadcom-ae",
                    placement: "homepage_projects",
                  });
                }
              }}
            >
              <div className="project-details">
                <div className="project-tags">
                  <span className="tag font-mono">FINTECH</span><span className="tag font-mono">BACKEND</span>
                </div>
                <h3 className="project-title font-mono">Sanadcom AE</h3>
                <p className="card-text">
                  A professional fintech platform for Moroccan auto-entrepreneurs, built to simplify compliance, automate invoicing, and eliminate administrative bottlenecks.
                </p>
                <span className="flex items-center gap-1 text-primary-glow font-bold font-mono mt-4">
                  READ CASE STUDY <ArrowRight className="w-4 h-4" />
                </span>
              </div>
              <div className="project-visual font-mono">
                SANADCOM_AE
              </div>
            </Link>

            <div className="project-card mt-8 opacity-60 border-dashed pointer-events-none cursor-default bg-slate-100/80 dark:bg-white/5 border border-slate-200 dark:border-white/10">
              <div className="project-details">
                <div className="project-tags">
                  <span className="tag font-mono bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">SaaS</span>
                  <span className="tag font-mono bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">Cloud</span>
                </div>
                <h3 className="project-title font-mono text-slate-900 dark:text-white">Enterprise Node</h3>
                <p className="card-text text-slate-600 dark:text-slate-400">
                  Infrastructure for scaling product operations, data pipelines, and customer-facing tools without the usual growth bottlenecks.
                </p>
                <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400 font-bold font-mono mt-4">
                  [NODE 02] UNDER DEPLOYMENT <Lock className="w-4 h-4" />
                </span>
              </div>
              <div className="project-visual bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 font-mono border border-slate-200 dark:border-white/10">
                NODE_02
              </div>
            </div>

            <div className="project-card mt-8 opacity-60 border-dashed pointer-events-none cursor-default bg-slate-100/80 dark:bg-white/5 border border-slate-200 dark:border-white/10">
              <div className="project-details">
                <div className="project-tags">
                  <span className="tag font-mono bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">AI</span>
                  <span className="tag font-mono bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">Inference</span>
                </div>
                <h3 className="project-title font-mono text-slate-900 dark:text-white">Inference Engine</h3>
                <p className="card-text text-slate-600 dark:text-slate-400">
                  Tailored AI workflows for analysis-heavy operations, reducing repetitive tasks and improving decision speed across teams.
                </p>
                <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400 font-bold font-mono mt-4">
                  [NODE 03] IN EXTRACTION <Lock className="w-4 h-4" />
                </span>
              </div>
              <div className="project-visual bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-slate-400 font-mono border border-slate-200 dark:border-white/10">
                NODE_03
              </div>
            </div>
          </div>
        </section>

        <section id="proof">
          <div className="container">
            <div className="section-header">
              <span className="section-tag font-mono">TRUST & PROCESS</span>
              <h2 className="section-title font-mono">HOW WE DE-RISK YOUR PROJECT</h2>
            </div>
            <div className="grid">
              <div className="glass-card">
                <div className="card-icon">
                  <Lock className="w-6 h-6" />
                </div>
                <h3 className="card-title font-mono">Clear Scope, Clear Ownership</h3>
                <p className="card-text">
                  Every engagement starts with a written scope, milestones, and delivery expectations so you know exactly what is being built and why.
                </p>
              </div>
              <div className="glass-card">
                <div className="card-icon">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="card-title font-mono">Fast, Structured Execution</h3>
                <p className="card-text">
                  We move in focused sprints with regular checkpoints, keeping momentum high while reducing rework and scope drift.
                </p>
              </div>
              <div className="glass-card">
                <div className="card-icon">
                  <Layers className="w-6 h-6" />
                </div>
                <h3 className="card-title font-mono">Built for Long-Term Scale</h3>
                <p className="card-text">
                  We prioritize resilient architecture and maintainable systems so your product can evolve without expensive rebuilds.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="insights">
          <div className="container">
            <div className="section-header">
              <span className="section-tag font-mono">ENGINEERING LOG</span>
              <h2 className="section-title font-mono">ARCHITECTURAL TRANSMISSIONS</h2>
            </div>

            <div className="grid">
              <Link href="/blog/cost-to-build-saas-mvp-2026" className="glass-card block text-inherit no-underline">
                <span className="text-xs font-mono text-primary-glow uppercase tracking-wider block mb-2">SaaS Strategy &bull; 2026-08-22</span>
                <h3 className="card-title font-mono text-lg mb-2">The True Cost to Build a SaaS MVP</h3>
                <p className="card-text text-sm">A transparent breakdown of SaaS MVP development costs, timelines, regional pricing, and budget traps.</p>
                <span className="flex items-center gap-1 text-primary-glow font-bold font-mono text-xs mt-4">
                  READ TRANSMISSION <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link href="/blog/reducing-technical-debt-cloud-systems" className="glass-card block text-inherit no-underline">
                <span className="text-xs font-mono text-primary-glow uppercase tracking-wider block mb-2">Architecture &bull; 2026-08-25</span>
                <h3 className="card-title font-mono text-lg mb-2">Reducing Technical Debt in Cloud Systems</h3>
                <p className="card-text text-sm">How to audit legacy architecture, eliminate performance bottlenecks, and build an actionable debt elimination roadmap.</p>
                <span className="flex items-center gap-1 text-primary-glow font-bold font-mono text-xs mt-4">
                  READ TRANSMISSION <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link href="/blog/ai-agent-vs-chatbot-enterprise-2026" className="glass-card block text-inherit no-underline">
                <span className="text-xs font-mono text-primary-glow uppercase tracking-wider block mb-2">AI Systems &bull; 2026-09-06</span>
                <h3 className="card-title font-mono text-lg mb-2">AI Agents vs Chatbots for Enterprise</h3>
                <p className="card-text text-sm">Why superficial chatbots fail and how autonomous, tool-using agents automate high-leverage business operations.</p>
                <span className="flex items-center gap-1 text-primary-glow font-bold font-mono text-xs mt-4">
                  READ TRANSMISSION <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>

            <div className="text-center mt-10">
              <Link href="/blog" className="btn btn-outline font-mono text-sm uppercase tracking-wider inline-flex items-center gap-2">
                All Engineering Logs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <section id="contact">
          <div className="container">
            <div className="contact-box">
              <span className="section-tag font-mono">START YOUR PROJECT</span>
              <h2 className="font-mono">READY TO TURN COMPLEXITY INTO GROWTH?</h2>
              <p className="contact-intro">Tell us what you are building, what is slowing you down, and when you need to launch. We will map the fastest path to a working solution.</p>
                <div className="booking-card">
                  <div className="card-icon mb-0"><Calendar className="w-8 h-8" /></div>
                  <h3 className="font-mono">Book a quick strategy call</h3>
                  <p>We will review your goals, technical constraints, and next steps without the fluff or hard sell.</p>
                  <button onClick={() => openBooking("contact_card")} className="btn btn-primary font-mono w-full justify-center p-4 text-base mt-auto">
                    BOOK A DISCOVERY CALL
                  </button>
                </div>
            </div>
          </div>
        </section>
      </main>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </>
  );
}
